/**
 * useRefundBookingSubmit — the submit lifecycle behind `RefundBookingDrawer`
 * (BON-147, BON-335): destination + required-reason validation, the POST
 * itself, and branching 409s into either the same "settled" outcome a fresh
 * page load would show (`ALREADY_REFUNDED`) or a destination-specific message
 * (`NO_PAYMENT_INTENT`) rather than a generic error. Split out of the drawer
 * purely to keep that component under the file's line-length cap — this hook
 * has exactly one caller.
 */
import { useEffect, useRef, useState } from 'react';

import {
  refundBooking,
  RefundBookingError,
  type BookingDetail,
  type RefundBookingResult,
  type RefundMethod,
} from '../../api/bookingsV2';
import { useDialogKeyboard } from '../../hooks/useDialogKeyboard';

import {
  CARD_UNAVAILABLE_REASON,
  NO_CASH_REASON,
  type UnavailableReasons,
} from './RefundDestinationChoice';

export type IssuedRefund = RefundBookingResult['refund'] & {
  giftCard: NonNullable<RefundBookingResult['refund']['giftCard']>;
  emailSent: boolean;
};

/** A gift-card refund always mints a card and either sends or fails to send
 *  the email — `giftCard` and `emailSent` are never null on this branch. That
 *  is a contract guarantee (BON-335 API round-1 outcome #7), not a runtime
 *  check, so this narrows on `method` rather than inspecting the fields. */
function isGiftCardRefund(refund: RefundBookingResult['refund']): refund is IssuedRefund {
  return refund.method === 'GIFT_CARD';
}

/** Three outcomes, keyed on `refund.method` rather than on the nullness of
 *  `giftCard`/`emailSent` — the null widening alone does not distinguish "no
 *  gift card because none was minted" from "no gift card because the field is
 *  merely typed loosely", and only the method says which is true.
 *
 *  `mismatch` (BON-335 review A1) is a fourth: the API and this app are
 *  separate Railway services deployed from the same merge with no ordering
 *  guarantee, and `develop`'s route currently parses the body with a
 *  non-strict `z.object({ reason })` that silently strips `method` — so a
 *  CSR who picked "Other" against a stale API gets a gift card minted and
 *  emailed instead. `refunds_immutable` and the `UNIQUE(booking_id)` index
 *  make that unrecoverable from here, so it must never render as `issued` or
 *  `recorded` — the requested destination and `refund.method` are compared
 *  after every 200, and a mismatch is its own outcome. */
export type RefundOutcome =
  | { kind: 'issued'; refund: IssuedRefund }
  | { kind: 'passes'; refund: RefundBookingResult['refund'] }
  | { kind: 'recorded'; refund: RefundBookingResult['refund'] }
  | { kind: 'settled' }
  | { kind: 'mismatch'; requested: RefundMethod; refund: RefundBookingResult['refund'] };

/** `externalReference` rides along only when there is something to send: a
 *  `GIFT_CARD` refund never carries one (a 400 if it did — A9), and an empty
 *  box on the other two destinations means "nothing to reference" rather than
 *  an empty string worth sending. */
function buildRefundInput(destination: RefundMethod, reason: string, reference: string) {
  const trimmedReference = takesReference(destination) ? reference.trim() : '';
  return trimmedReference
    ? { method: destination, reason, externalReference: trimmedReference }
    : { method: destination, reason };
}

/** Only a refund recorded against money that already moved has a reference. */
export function takesReference(destination: RefundMethod): boolean {
  return destination === 'CARD' || destination === 'EXTERNAL';
}

/** The refund that mints something for the customer: passes while they are
 *  on, a gift card while they are off. */
export function mintingDestination(giftPassesEnabled: boolean | null): RefundMethod {
  return giftPassesEnabled ? 'GIFT_PASS' : 'GIFT_CARD';
}

function outcomeFor(refund: RefundBookingResult['refund']): RefundOutcome {
  if (isGiftCardRefund(refund)) return { kind: 'issued', refund };
  if (refund.method === 'GIFT_PASS') return { kind: 'passes', refund };
  return { kind: 'recorded', refund };
}

type RefundErrorKind = 'already-refunded' | 'no-payment-intent' | 'method-unavailable' | 'other';

/** Both `RefundBookingError` branches below are booking-state races rather
 *  than user mistakes — `ALREADY_REFUNDED` settles like a fresh page load
 *  would, `NO_PAYMENT_INTENT` reads as stale client data (F2 already disables
 *  the option whenever the booking itself has no charge). Everything else is
 *  a real error the CSR has to see. Pulled out of `submit` to keep both that
 *  function's line count and its branching under the file's caps. */
function classifyRefundError(err: unknown): { kind: RefundErrorKind; message: string } {
  if (err instanceof RefundBookingError && err.code === 'ALREADY_REFUNDED') {
    return { kind: 'already-refunded', message: err.message };
  }
  if (err instanceof RefundBookingError && err.code === 'NO_PAYMENT_INTENT') {
    return { kind: 'no-payment-intent', message: err.message };
  }
  if (err instanceof RefundBookingError && err.code === 'REFUND_METHOD_UNAVAILABLE') {
    return { kind: 'method-unavailable', message: err.message };
  }
  return {
    kind: 'other',
    message: err instanceof Error ? err.message : 'Could not refund this booking.',
  };
}

/** Builds the `submit` handler outside the hook body so the hook itself stays
 *  under the file's `max-lines-per-function` cap — this is a plain closure
 *  factory, not a hook (no hook calls inside), so calling it once per render
 *  is exactly as cheap as defining it inline was. */
function buildSubmitHandler(args: {
  booking: BookingDetail;
  destination: RefundMethod;
  reason: string;
  reference: string;
  canSubmit: boolean;
  /** What to tell the CSR when they submit while `canSubmit` is false. */
  blockedMessage: string;
  onRefunded: () => void;
  setSubmitting: (v: boolean) => void;
  setError: (v: string | null) => void;
  setOutcome: (o: RefundOutcome) => void;
  setCardBlockedByServer: (v: boolean) => void;
  /** The server refused gift card or gift passes because the switch flipped
   *  since this drawer loaded: offer the other one instead. */
  onMintingDestinationRefused: (refused: RefundMethod) => void;
}) {
  const {
    booking,
    destination,
    reason,
    reference,
    canSubmit,
    blockedMessage,
    onRefunded,
    setSubmitting,
    setError,
    setOutcome,
    setCardBlockedByServer,
    onMintingDestinationRefused,
  } = args;
  return async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) {
      setError(blockedMessage);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const input = buildRefundInput(destination, reason.trim(), reference);
      const result = await refundBooking(booking.id, input);
      // Refetch regardless of what follows: the booking really did change
      // server-side (REFUNDED, cash zeroed) even on the mismatch branch below.
      onRefunded();
      if (result.refund.method !== destination) {
        setOutcome({ kind: 'mismatch', requested: destination, refund: result.refund });
        return;
      }
      setOutcome(outcomeFor(result.refund));
    } catch (err) {
      const classified = classifyRefundError(err);
      if (classified.kind === 'already-refunded') {
        onRefunded();
        setOutcome({ kind: 'settled' });
        return;
      }
      if (classified.kind === 'no-payment-intent') {
        setCardBlockedByServer(true);
        return;
      }
      if (classified.kind === 'method-unavailable') onMintingDestinationRefused(destination);
      setError(classified.message);
    } finally {
      // Always, not just on the error path. `submitting` disables the close
      // button, suppresses Escape and blocks the backdrop click — all correct
      // while the request is in flight, and all wrong once the receipt is on
      // screen. Leaving it set left the finished drawer dismissable only by
      // its "Done" button, with a visibly greyed-out close control.
      setSubmitting(false);
    }
  };
}

/** Destinations this booking can't use. A booking with no cash on it can only
 *  get gift passes; "Original card" also needs a Stripe charge on file, or the
 *  server refused one at submit — both read as the same fact. */
function unavailableDestinations(
  booking: BookingDetail,
  cardBlockedByServer: boolean,
): UnavailableReasons {
  if (!booking.money.amountPaidCents) {
    return { GIFT_CARD: NO_CASH_REASON, CARD: NO_CASH_REASON, EXTERNAL: NO_CASH_REASON };
  }
  const hasPaymentIntent = Boolean(booking.stripe?.paymentIntentId);
  return !hasPaymentIntent || cardBlockedByServer ? { CARD: CARD_UNAVAILABLE_REASON } : {};
}

/**
 * The chosen destination, plus which minting destination is on offer. Starts
 * on whichever of gift passes or gift card the server has switched on, and
 * swaps to the other if the server refuses the one shown.
 */
function useRefundDestination(loadedGiftPassesEnabled: boolean | null) {
  // Set only when the server refuses the option shown, overriding the page's reading.
  const [correction, setCorrection] = useState<boolean | null>(null);
  const giftPassesEnabled = correction ?? loadedGiftPassesEnabled;
  const [destination, setDestination] = useState<RefundMethod>('GIFT_CARD');
  useEffect(() => {
    if (giftPassesEnabled === null) return;
    setDestination((current) =>
      takesReference(current) ? current : mintingDestination(giftPassesEnabled),
    );
  }, [giftPassesEnabled]);
  const onMintingDestinationRefused = (refused: RefundMethod) => {
    if (!takesReference(refused)) setCorrection(refused === 'GIFT_CARD');
  };
  return { giftPassesEnabled, destination, setDestination, onMintingDestinationRefused };
}

export function useRefundBookingSubmit(
  booking: BookingDetail,
  onClose: () => void,
  onRefunded: () => void,
  loadedGiftPassesEnabled: boolean | null,
) {
  const [reason, setReasonState] = useState('');
  const {
    giftPassesEnabled,
    destination,
    setDestination: setDestinationState,
    onMintingDestinationRefused,
  } = useRefundDestination(loadedGiftPassesEnabled);
  const [reference, setReference] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Blocked by the booking's own data (no Stripe charge on file) OR by the
  // server refusing a CARD attempt after the fact — a race between stale
  // client data and the server's own check inside the lock. Both read the
  // same fact, so both show the same text (F2/F7).
  const [cardBlockedByServer, setCardBlockedByServer] = useState(false);
  const [outcome, setOutcome] = useState<RefundOutcome | null>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  useDialogKeyboard(onClose, reasonRef, submitting);

  const unavailableReasons = unavailableDestinations(booking, cardBlockedByServer);

  const canSubmit =
    reason.trim().length > 0 &&
    !submitting &&
    giftPassesEnabled !== null &&
    unavailableReasons[destination] === undefined;
  const blockedMessage = !reason.trim()
    ? 'Please say why this booking is being refunded.'
    : (unavailableReasons[destination] ??
      'Refund options are still loading. Try again in a moment.');

  const setReason = (v: string) => {
    setReasonState(v);
    if (error) setError(null);
  };

  const setDestination = (method: RefundMethod) => {
    setDestinationState(method);
    setReference('');
    if (error) setError(null);
  };

  const submit = buildSubmitHandler({
    booking,
    destination,
    reason,
    reference,
    canSubmit,
    blockedMessage,
    onRefunded,
    setSubmitting,
    setError,
    setOutcome,
    setCardBlockedByServer,
    onMintingDestinationRefused,
  });

  const dismiss = () => {
    if (!submitting) onClose();
  };

  return {
    reason,
    setReason,
    giftPassesEnabled,
    destination,
    setDestination,
    reference,
    setReference,
    unavailableReasons,
    submitting,
    error,
    outcome,
    canSubmit,
    reasonRef,
    submit,
    dismiss,
  };
}
